import PalommaLogoSmall from "@/assets/PalommaLogoSmall.jpg";
import { ROUTES } from "@/config/routes";
import { useSideMenu } from "@/contexts/SideMenuContext";
import { useFetchUser } from "@/hooks/useFetchUser";
import { useMerchant } from "@/hooks/useMerchant";
import { usePortalContext } from "@/hooks/usePortalContext";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { orgNav, merchantNav } from "@/config/navigation";
import { Box, Button, Image, VStack } from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  Link2,
  LogOut,
  Settings,
  User,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PalommaLogoWithText from "../assets/PalommaLogoWithText.svg";
import { usePermissions } from "@/hooks/usePermissions";
import { resetUser } from "@/helpers/posthog";
import ProtectMenuSection from "@/protect/ProtectMenuSection";
import NavigationSection from "./NavigationSection";
import ProfileModal from "./ProfileModal";
import SelectContextModal from "./SelectContextModal";
import ContextSelectorCard from "./ContextSelectorCard";
import SettingsModal from "./SettingsModal";
import { toaster } from "./ui/toaster";

const SideMenu = (): JSX.Element => {
  const { isSideMenuOpen: isOpen, setIsSideMenuOpen: setIsOpen } =
    useSideMenu();
  const [isContextSelectorOpen, setIsContextSelectorOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const { isMerchantAdmin, isDeveloper } = usePermissions();
  const stytch = useStytch();
  const navigate = useNavigate();
  const { user, isLoading: isUserLoading } = useFetchUser();
  const { merchant } = useMerchant();
  const { context } = usePortalContext();
  const isOrgAdmin = useIsOrgAdmin();

  const handleLogout = useCallback(async () => {
    resetUser();
    await stytch.session.revoke();
    navigate(ROUTES.AUTH.LOGIN);
  }, [stytch, navigate]);

  const upperSection = useMemo(
    () =>
      context?.type === "org" ? orgNav({ isOrgAdmin }) : merchantNav(merchant),
    [context?.type, isOrgAdmin, merchant]
  );

  const sideMenuBottomSection = [
    {
      id: "tutorials",
      path: ROUTES.DASHBOARD.TUTORIALS,
      section: "Tutoriales",
      icon: GraduationCap,
    },
    ...(merchant?.paymentsUrl
      ? [
          {
            id: "copyPaymentsLink",
            path: "#",
            section: "Copiar enlace de pagos",
            icon: Link2,
            handler: "copyPaymentsLink",
          },
        ]
      : []),
    ...(isMerchantAdmin || isDeveloper
      ? [
          {
            id: "settings",
            path: "#",
            section: "Configuración",
            icon: Settings,
            handler: "settings",
          },
        ]
      : []),
    {
      isDisabled: isUserLoading,
      id: "profile",
      path: "#",
      section: "Perfil",
      icon: User,
      handler: "profile",
      showBadge: !user?.displayName || !user?.phoneNumber,
      tooltipText: "Recuerda completar tu información",
    },
    {
      id: "logout",
      path: ROUTES.DASHBOARD.LOGOUT,
      section: "Cerrar sesión",
      icon: LogOut,
      handler: "logout",
    },
  ];

  const handleAction = useCallback(
    (handler?: string) => {
      if (handler === "logout") {
        handleLogout();
      } else if (handler === "profile") {
        setIsProfileModalOpen(true);
      } else if (handler === "settings") {
        setIsSettingsModalOpen(true);
      } else if (handler === "copyPaymentsLink" && merchant?.paymentsUrl) {
        navigator.clipboard
          .writeText(merchant.paymentsUrl)
          .then(() =>
            toaster.create({
              title: "Enlace copiado",
              description:
                "Compártalo con los inquilinos para que paguen en línea.",
              type: "success",
            })
          )
          .catch(() =>
            toaster.create({
              title: "Error",
              description: "No se pudo copiar el enlace. Intente de nuevo.",
              type: "error",
            })
          );
      }
    },
    [handleLogout, setIsProfileModalOpen, merchant?.paymentsUrl]
  );

  return (
    <>
      <Box
        height={"100vh"}
        display={"flex"}
        width={isOpen ? "250px" : "80px"}
        transition="width 0.3s"
        borderRight="1.5px solid #ebecef80"
        position="relative"
        zIndex={0}
      >
        <Box
          width="100%"
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-between"}
          zIndex={0}
        >
          <VStack p={4} align="stretch">
            <Box
              height={isOpen ? "60px" : "40px"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={2}
            >
              {isOpen ? (
                <Image
                  src={PalommaLogoWithText}
                  alt="Palomma"
                  height="40px"
                  objectFit="contain"
                  transition="all 0.3s"
                />
              ) : (
                <Image
                  src={PalommaLogoSmall}
                  alt="Palomma"
                  height="100%"
                  objectFit="contain"
                  borderRadius="md"
                  transition="all 0.3s"
                />
              )}
            </Box>
            <ContextSelectorCard
              onOpen={() => setIsContextSelectorOpen(true)}
            />
            {upperSection.map((ele) => (
              <NavigationSection
                key={ele.path}
                path={ele.path}
                section={ele.section}
                showText={isOpen}
                icon={ele.icon}
              />
            ))}
            <ProtectMenuSection showText={isOpen} />
          </VStack>
          <VStack p={4} align="stretch">
            {sideMenuBottomSection.map((ele) => (
              <NavigationSection
                key={ele.id}
                path={`${ele.path}`}
                section={ele.section}
                showText={isOpen}
                icon={ele.icon}
                isDisabled={ele.isDisabled}
                onClick={() => handleAction(ele.handler)}
                disableActiveStyle={!!ele.handler}
                showBadge={ele.showBadge}
                tooltipText={ele.tooltipText}
                tourId={ele.id}
              />
            ))}
          </VStack>
        </Box>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          alignSelf={"center"}
          position="absolute"
          right="-12px"
          p={0}
          m={0}
          background="white"
          borderRadius="full"
          border="1px solid"
          borderColor="#ebecef"
          width="24px"
          height="24px"
          minW="24px"
          _hover={{ background: "palomma.500", cursor: "pointer" }}
          zIndex={1}
        >
          {isOpen ? (
            <ArrowLeft size={16} color="#e5e7eb" />
          ) : (
            <ArrowRight size={16} color="#e5e7eb" />
          )}
        </Button>
      </Box>

      <SelectContextModal
        open={isContextSelectorOpen}
        onClose={() => setIsContextSelectorOpen(false)}
      />
      <ProfileModal
        open={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
      <SettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </>
  );
};

export default SideMenu;
